<script setup>
import { ref, onMounted, computed } from 'vue';
import CalendarButtons from './CalendarButtons.vue';

// API-Basis-URL (gleich wie in Admin-Komponente)
const API_URL = '/api';

// Shows-Daten
const props = defineProps({
  shows: {
    type: Array,
    required: true
  },
  calendar_image: {
    type: String,
    default: '/upload/homeward-2023-onlinekalender.jpg'
  }
});

// Filtere nur zukünftige Termine
const upcomingShows = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Setze Zeit auf Mitternacht für genauen Tagesvergleich

  return props.shows.filter(show => {
    const showDate = parseLocalDate(show.date);
    return showDate >= today;
  });
});

// Gruppierte Shows nach Monaten
const groupedShows = computed(() => {
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const groups = {};
  upcomingShows.value.forEach(show => {
    const date = parseLocalDate(show.date);
    const month = date.getMonth();
    if (!groups[month]) {
      groups[month] = {
        monthName: months[month],
        shows: []
      };
    }
    groups[month].shows.push({
      ...show,
      day: date.getDate(),
      month: months[month]
    });
  });
  
  return Object.values(groups).sort((a, b) => {
    const monthA = months.indexOf(a.monthName);
    const monthB = months.indexOf(b.monthName);
    return monthA - monthB;
  });
});

// Offene Monate verwalten
const openMonths = ref({});

// Monat umschalten
const toggleMonth = (index) => {
  openMonths.value[index] = !openMonths.value[index];
};

// Aktuellen Monat automatisch öffnen
onMounted(() => {
  if (groupedShows.value.length > 0) {
    // Ersten Monat standardmäßig öffnen
    openMonths.value[0] = true;
    
    // Optional: Wenn es aktuelle Shows im aktuellen Monat gibt, diesen öffnen
    const currentMonth = new Date().getMonth();
    const monthIndex = groupedShows.value.findIndex(group => 
      parseLocalDate(group.shows[0].date).getMonth() === currentMonth
    );
    if (monthIndex !== -1) {
      openMonths.value[monthIndex] = true;
    }
  }
});

const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Extrahiere Tag aus dem Datum
const getDay = (dateString) => {
  return parseLocalDate(dateString).getDate();
};

// Extrahiere Monat aus dem Datum
const getMonth = (dateString) => {
  const date = parseLocalDate(dateString);
  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return monthNames[date.getMonth()];
};
</script>

<template>
  <section id="shows" class="shows">
    <div class="container">
      <div class="section-header">
        <h2>Auftritte</h2>
        <div class="celtic-divider">
          <span></span><svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" /></svg><span></span>
        </div>
      </div>

      <div class="shows-content">
        <div class="shows-image">
          <img :src="calendar_image" alt="HomeWard Auftritte Kalender" class="calendar-img">
        </div>
        
        <div class="shows-data">
          <div v-if="upcomingShows.length === 0" class="empty-state">
            <p>Aktuell sind keine Termine geplant.</p>
          </div>
          
          <div v-else class="shows-list">
            <div v-for="(month, index) in groupedShows" :key="index" class="month-group">
              <button 
                class="month-header" 
                @click="toggleMonth(index)"
                :class="{ 'active': openMonths[index] }"
              >
                <span class="month-name">{{ month.monthName }}</span>
                <span class="show-count">{{ month.shows.length }} Termine</span>
                <svg class="arrow-icon" :class="{ 'rotated': openMonths[index] }" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M19 9l-7 7-7-7" stroke="currentColor" stroke-width="2" fill="none"/>
                </svg>
              </button>
              <div class="month-shows" v-show="openMonths[index]">
                <div v-for="show in month.shows" :key="show.id" class="show-card">
                  <div class="show-date">
                    <span class="day">{{ show.day }}</span>
                    <span class="month">{{ show.month }}</span>
                  </div>
                  <div class="show-info">
                    <h3>{{ show.venue }}</h3>
                    <p>{{ show.location }}</p>
                    <div v-if="show.show_time" class="show-time">
                      <i class="fas fa-clock"></i>
                      <span>{{ show.show_time }} Uhr</span>
                    </div>
                    <div class="show-actions">
                      <a v-if="show.ticketLink" :href="show.ticketLink" class="btn btn-small" target="_blank">Tickets</a>
                      <CalendarButtons 
                        :showId="show.id" 
                        :venue="show.venue" 
                        :date="show.date" 
                        :location="show.location"
                        :time="show.show_time"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  max-width: 300px;
}

.celtic-divider span {
  flex: 1;
  height: 1px;
  background-color: var(--primary-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 15px;
  color: var(--primary-color);
  opacity: 0.7;
}

.shows-content {
  display: grid;
  grid-template-columns: 40% 60%;
  gap: 4rem;
  margin-bottom: 2rem;
}

.shows-image {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.calendar-img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.calendar-img:hover {
  transform: scale(1.02);
}

.shows-data {
  width: 100%;
}

.shows-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.month-group {
  margin-bottom: 1rem;
}

.month-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background-color: var(--white);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.month-header:hover {
  background-color: var(--bg-alt);
}

.month-header.active {
  background-color: var(--primary-color);
  color: white;
}

.month-name {
  font-weight: 600;
  font-size: 1.1rem;
}

.show-count {
  font-size: 0.9rem;
  opacity: 0.8;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.arrow-icon.rotated {
  transform: rotate(180deg);
}

.month-shows {
  background-color: var(--white);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  margin-top: 0.5rem;
}

.show-card {
  display: flex;
  gap: 1.5rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.show-card:last-child {
  border-bottom: none;
}

.show-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 70px;
}

.day {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
}

.month {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.show-info {
  flex: 1;
}

.show-info h3 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.show-info p {
  font-size: 0.9rem;
  margin-bottom: 1rem;
  color: var(--text-muted);
}

.show-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  color: var(--primary-color);
  font-weight: 500;
}

.show-time i {
  font-size: 1rem;
}

.show-time span {
  font-size: 0.95rem;
}

.show-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 0.4rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
}

.btn:hover {
  background-color: var(--primary-dark);
}

.btn-small {
  padding: 0.4rem 0.75rem;
  font-size: 0.9rem;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background-color: var(--white);
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
}

@media (max-width: 992px) {
  .shows-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .shows-image {
    max-width: 400px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .month-header {
    padding: 0.75rem 1rem;
  }
  
  .month-shows {
    padding: 0.75rem 1rem;
  }
  
  .show-card {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 0;
  }
  
  .show-date {
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-start;
  }
  
  .day {
    font-size: 1.5rem;
  }
  
  .month {
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .section-header h2 {
    font-size: 2.5rem;
  }
  
  .show-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .btn, .calendar-btn {
    width: 100%;
  }
}
</style> 